/* Size guide pop-up. Loaded by snippets/size-guide-modal.liquid, no dependencies.

   What it does:
   - opens the dialog from any control with data-size-guide-open, closes on the close button, on
     Escape and on a click on the backdrop, keeps focus inside and returns it to the opener;
   - switches the displayed units between metric and imperial, including the numbers in the table,
     which carry their source value in data-raw;
   - suggests a size from the supplier ranges in the size_chart metafield: pick the row the weight
     or the chest measurement falls in, then move one size up or down when the height falls outside
     that row's height range. Which of the two it asks for follows the chart: weight when the rows
     carry weight_kg, chest when they carry chest_cm instead, because a supplier who gives a body
     chart in centimetres gives no weight. No BMI, no bands of our own, no number that is not in the
     chart. Outside the chart it says so and points to the support mailbox.

   Without JavaScript the dialog never opens, the fit finder stays hidden and the size guide link
   in the mobile menu keeps working as a link to the size guide page. */
(function () {
  'use strict';

  var dialog = document.querySelector('[data-size-guide]');
  if (!dialog) return;

  /* Every word comes from window.storeStrings, which layout/theme.liquid renders from the locale
     files. A word between percent signs, such as %size%, is a placeholder that fill() replaces. */
  var S = window.storeStrings || {};

  var closeBtn = dialog.querySelector('[data-size-guide-close]');
  var finder = dialog.querySelector('[data-size-guide-finder]');
  var unitSwitch = dialog.querySelector('[data-size-guide-unit-switch]');
  var resultEl = dialog.querySelector('[data-size-guide-result]');
  var chartEl = dialog.querySelector('script[data-size-guide-chart]');
  var chartUnit = (dialog.getAttribute('data-chart-unit') || 'cm').toLowerCase();
  var sizeIndex = parseInt(dialog.getAttribute('data-size-index'), 10);
  var email = dialog.getAttribute('data-email') || '';
  var display = dialog.getAttribute('data-units') === 'imperial' ? 'imperial' : 'metric';
  var opener = null;
  var focusAfterClose = null;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var chart = null;
  try {
    chart = chartEl ? JSON.parse(chartEl.textContent) : null;
  } catch (err) {
    chart = null;
  }

  /* ---- helpers ------------------------------------------------------------------------- */
  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  function fill(text, values) {
    return String(text).replace(/%(\w+)%/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match;
    });
  }

  function isRange(value) {
    return Object.prototype.toString.call(value) === '[object Array]' && value.length === 2 && isFinite(value[0]) && isFinite(value[1]);
  }

  /* Which second measurement the chart carries next to the height range. Weight keeps priority, so
     a chart that holds both behaves exactly as it did before the chest ranges existed. */
  function chartKey() {
    if (!chart || !chart.rows || !chart.rows.length) return null;
    var keys = ['weight_kg', 'chest_cm'];
    for (var k = 0; k < keys.length; k++) {
      for (var i = 0; i < chart.rows.length; i++) {
        var row = chart.rows[i];
        if (row && row.size && isRange(row.height_cm) && isRange(row[keys[k]])) return keys[k];
      }
    }
    return null;
  }

  function usableRows() {
    var key = chartKey();
    if (!key) return [];
    var out = [];
    for (var i = 0; i < chart.rows.length; i++) {
      var row = chart.rows[i];
      if (row && row.size && isRange(row.height_cm) && isRange(row[key])) out.push(row);
    }
    return out;
  }

  function round(value, decimals) {
    var factor = Math.pow(10, decimals);
    return String(Math.round(value * factor) / factor);
  }

  function convertNumbers(text, factor, decimals) {
    return String(text).replace(/\d+(?:[.,]\d+)?/g, function (match) {
      var n = parseFloat(match.replace(',', '.'));
      if (!isFinite(n)) return match;
      return round(n * factor, decimals);
    });
  }

  /* ---- open and close ------------------------------------------------------------------ */
  function openDialog(trigger) {
    if (dialog.open) return;
    opener = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.classList.add('is-fallback');
      dialog.setAttribute('open', '');
    }
    document.body.classList.add('size-guide-open');
    if (closeBtn) closeBtn.focus();
  }

  function closeDialog() {
    if (!dialog.open) return;
    if (typeof dialog.close === 'function' && !dialog.classList.contains('is-fallback')) {
      dialog.close();
      return;
    }
    dialog.removeAttribute('open');
    cleanup();
  }

  function cleanup() {
    document.body.classList.remove('size-guide-open');
    var target = focusAfterClose || opener;
    focusAfterClose = null;
    opener = null;
    if (target && typeof target.focus === 'function') {
      try {
        target.focus({ preventScroll: true });
      } catch (err) {
        target.focus();
      }
    }
  }

  function trapFocus(event) {
    var nodes = [];
    var all = dialog.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hidden && all[i].offsetParent !== null) nodes.push(all[i]);
    }
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  dialog.addEventListener('close', cleanup);

  /* ---- units --------------------------------------------------------------------------- */
  function setUnits(next) {
    display = next === 'imperial' ? 'imperial' : 'metric';
    var buttons = dialog.querySelectorAll('[data-size-guide-units]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-pressed', buttons[i].getAttribute('data-size-guide-units') === display ? 'true' : 'false');
    }
    var groups = dialog.querySelectorAll('[data-size-guide-fields]');
    for (var j = 0; j < groups.length; j++) {
      groups[j].hidden = groups[j].getAttribute('data-size-guide-fields') !== display;
    }
    convertTable();
  }

  function convertTable() {
    var toImperial = chartUnit === 'cm' && display === 'imperial';
    var toMetric = chartUnit === 'in' && display === 'metric';
    var cells = dialog.querySelectorAll('[data-size-guide-cell]');
    for (var i = 0; i < cells.length; i++) {
      var raw = cells[i].getAttribute('data-raw') || '';
      if (toImperial) cells[i].textContent = convertNumbers(raw, 1 / 2.54, 1);
      else if (toMetric) cells[i].textContent = convertNumbers(raw, 2.54, 0);
      else cells[i].textContent = raw;
    }
    var heads = dialog.querySelectorAll('[data-size-guide-head]');
    for (var j = 0; j < heads.length; j++) {
      var label = heads[j].getAttribute('data-raw') || '';
      if (toImperial) label = label.replace(/\bcm\b/gi, S.sizeGuideUnitIn).replace(/\bcentimetres\b/gi, S.sizeGuideInches);
      /* Only the unit in brackets, so a heading like "Length in body" keeps its wording. */
      if (toMetric) label = label.replace(/\binches\b/gi, S.sizeGuideCentimetres).replace(/\(in\)/gi, '(cm)');
      heads[j].textContent = label;
    }
  }

  /* ---- fit finder ---------------------------------------------------------------------- */
  function readField(key) {
    var input = dialog.querySelector('[data-size-guide-input="' + key + '"]');
    if (!input || input.value === '') return null;
    var n = parseFloat(String(input.value).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  /* Both shapes hand back the height in centimetres and the second measurement in the unit the
     chart uses: kilograms for a weight chart, centimetres for a chest chart. */
  function readBody(key) {
    if (display === 'imperial') {
      var feet = readField('height_ft');
      var inches = readField('height_in');
      var second = key === 'chest_cm' ? readField('chest_in') : readField('weight_lb');
      if (feet === null || second === null) return null;
      if (inches === null) inches = 0;
      return {
        height: (feet * 12 + inches) * 2.54,
        measure: key === 'chest_cm' ? second * 2.54 : second / 2.20462
      };
    }
    var cm = readField('height_cm');
    var metric = key === 'chest_cm' ? readField('chest_cm') : readField('weight_kg');
    if (cm === null || metric === null) return null;
    return { height: cm, measure: metric };
  }

  function findSizeControl(size) {
    if (!(sizeIndex >= 0)) return null;
    var wanted = String(size).trim().toLowerCase();
    var select = document.querySelector('select[data-option-index="' + sizeIndex + '"]');
    if (select) {
      for (var i = 0; i < select.options.length; i++) {
        var option = select.options[i];
        var byValue = String(option.value).trim().toLowerCase();
        var byText = String(option.textContent).trim().toLowerCase();
        if (byValue === wanted || byText === wanted) return { type: 'select', el: select, option: option };
      }
      return null;
    }
    var radios = document.querySelectorAll('input[data-option-index="' + sizeIndex + '"]');
    for (var j = 0; j < radios.length; j++) {
      if (String(radios[j].value).trim().toLowerCase() === wanted) return { type: 'radio', el: radios[j] };
    }
    return null;
  }

  function addChooseButton(size) {
    if (!findSizeControl(size)) return;
    var button = el('button', 'btn btn--ghost btn--small sg__choose', fill(S.sizeGuideChooseSize, { size: size }));
    button.type = 'button';
    button.setAttribute('data-size-guide-choose', size);
    resultEl.appendChild(button);
  }

  function applySize(size) {
    var control = findSizeControl(size);
    if (!control) {
      closeDialog();
      return;
    }
    if (control.type === 'select') control.el.value = control.option.value;
    else control.el.checked = true;
    control.el.dispatchEvent(new Event('change', { bubbles: true }));
    focusAfterClose = control.el;
    closeDialog();
  }

  function showMessage(text) {
    resultEl.innerHTML = '';
    resultEl.appendChild(el('p', '', text));
  }

  function showOutOfRange() {
    resultEl.innerHTML = '';
    var p = el('p', '', '');
    p.appendChild(document.createTextNode(S.sizeGuideOutOfRange + ' '));
    if (email) {
      p.appendChild(document.createTextNode(S.sizeGuideOutOfRangeEmail + ' '));
      var mail = el('a', '', email);
      mail.href = 'mailto:' + email;
      p.appendChild(mail);
    } else {
      var contact = el('a', '', S.contactUs);
      contact.href = '/pages/contact';
      p.appendChild(contact);
    }
    p.appendChild(document.createTextNode(' ' + S.sizeGuideOutOfRangeSuffix));
    resultEl.appendChild(p);
  }

  function showSize(size) {
    resultEl.innerHTML = '';
    resultEl.appendChild(el('span', 'sg__badge', size));
    resultEl.appendChild(el('p', '', S.sizeGuideResultSingle));
    addChooseButton(size);
  }

  function showBetween(lower, upper) {
    resultEl.innerHTML = '';
    resultEl.appendChild(el('span', 'sg__badge', fill(S.sizeGuideBetweenSizes, { lower: lower, upper: upper })));
    resultEl.appendChild(el('p', '', S.sizeGuideResultBetween));
    addChooseButton(upper);
  }

  /* A chest measurement can land in the gap between two supplier ranges, and that is a real
     answer: name the two sizes it sits between. Below the smallest or above the largest range
     there is nothing to say, and a weight that matches no row stays outside the chart as before. */
  function gapBetween(rows, key, value) {
    for (var i = 0; i < rows.length - 1; i++) {
      if (value > rows[i][key][1] && value < rows[i + 1][key][0]) return i;
    }
    return -1;
  }

  function suggest() {
    if (!resultEl) return;
    var key = chartKey();
    var rows = usableRows();
    if (!rows.length) return;
    var body = readBody(key);
    if (!body || body.height <= 0 || body.measure <= 0) {
      showMessage(key === 'chest_cm' ? S.sizeGuideIntroChest : S.sizeGuideIntroWeight);
      return;
    }
    var index = -1;
    for (var i = 0; i < rows.length; i++) {
      if (body.measure >= rows[i][key][0] && body.measure <= rows[i][key][1]) {
        index = i;
        break;
      }
    }
    if (index === -1) {
      var gap = key === 'chest_cm' ? gapBetween(rows, key, body.measure) : -1;
      if (gap === -1) {
        showOutOfRange();
        return;
      }
      showBetween(rows[gap].size, rows[gap + 1].size);
      return;
    }
    var row = rows[index];
    var other = -1;
    if (body.height > row.height_cm[1] && index < rows.length - 1) other = index + 1;
    else if (body.height < row.height_cm[0] && index > 0) other = index - 1;
    if (other === -1) {
      showSize(row.size);
      return;
    }
    showBetween(rows[Math.min(index, other)].size, rows[Math.max(index, other)].size);
  }

  /* ---- events -------------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var trigger = event.target.closest('[data-size-guide-open]');
    if (trigger) {
      event.preventDefault();
      /* The mobile menu holds one of these controls; leaving the drawer open behind the pop-up
         would put the visitor back in the menu when they close it. */
      var drawer = trigger.closest('details[data-menu]');
      if (drawer) drawer.removeAttribute('open');
      openDialog(trigger);
      return;
    }
    if (!dialog.open) return;
    if (event.target.closest('[data-size-guide-close]')) {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (event.target === dialog) {
      closeDialog();
      return;
    }
    var unitButton = event.target.closest('[data-size-guide-units]');
    if (unitButton) {
      setUnits(unitButton.getAttribute('data-size-guide-units'));
      return;
    }
    if (event.target.closest('[data-size-guide-submit]')) {
      event.preventDefault();
      suggest();
      return;
    }
    var choose = event.target.closest('[data-size-guide-choose]');
    if (choose) {
      event.preventDefault();
      applySize(choose.getAttribute('data-size-guide-choose'));
    }
  });

  /* Escape is closed here rather than left to the browser, so the fallback path and the native
     dialog behave the same and the body scroll lock is always released. */
  document.addEventListener('keydown', function (event) {
    if (!dialog.open) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (event.key === 'Tab') trapFocus(event);
  });

  dialog.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (event.target && event.target.getAttribute && event.target.getAttribute('data-size-guide-input') !== null) {
      event.preventDefault();
      suggest();
    }
  });

  /* ---- start --------------------------------------------------------------------------- */
  if (unitSwitch) unitSwitch.hidden = false;
  if (finder && usableRows().length) finder.hidden = false;
  convertTable();
})();
